
import React from 'react';
import { motion } from 'framer-motion';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import Seo from '@/components/Seo.jsx';

function PracticalInfoPage() {
  return (
    <>
      <Seo
        title="Infos Pratiques - Neuvième Continent"
        description="Horaires, localisation et contact de Neuvième Continent. Toutes les informations pratiques pour nous rejoindre et participer à nos sessions de jeu de rôle."
      />

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-grow">
          {/* Hero Section */}
          <section 
            className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden"
            style={{
              background: 'linear-gradient(to bottom, #EF509D, #f27277)'
            }}
          >
            <div className="max-w-7xl mx-auto relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <h1 className="font-pixel text-5xl md:text-6xl lg:text-7xl text-white mb-6 drop-shadow-lg tracking-wide">
                  Nous rendre visite
                </h1>
                <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium drop-shadow-md">
                  Toutes les informations pour nous rejoindre et participer à nos activités
                </p>
              </motion.div>
            </div>
          </section>

          {/* Notre Local Section (Combined Address, Hours, Contact with Map) */}
          {/* overflow-x-clip : le sticker de la carte déborde volontairement,
              sans créer de défilement horizontal sur mobile */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#E8D5F2] overflow-x-clip">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                
                {/* Left Column - Content (approx 35%) */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="lg:col-span-4 space-y-10"
                >
                  <h2 className="font-y2k text-3xl md:text-4xl font-bold text-[#7A4B9E] uppercase tracking-wide">
                    Notre Local
                  </h2>
                  
                  {/* Address Block */}
                  <div className="flex items-start gap-4">
                    <img 
                      src="https://horizons-cdn.hostinger.com/257434f3-17c6-40ce-944a-86edaf1db41d/af820f3fadd72cdcdce3a2a11dfad961.png" 
                      alt="Icône adresse" 
                      className="w-10 h-10 flex-shrink-0 mt-1 object-contain"
                    />
                    <div>
                      <h3 className="font-y2k text-xl font-bold text-gray-900 mb-2">Adresse</h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        Collectif Plaisance<br />
                        4 allée de l'Ille<br />
                        44700 ORVAULT
                      </p>
                    </div>
                  </div>

                  {/* Hours Block */}
                  <div className="flex items-start gap-4">
                    <img 
                      src="https://horizons-cdn.hostinger.com/257434f3-17c6-40ce-944a-86edaf1db41d/3d0ddd3483cb024ee6768cd1d4a11a2e.png" 
                      alt="Icône horaires" 
                      className="w-10 h-10 flex-shrink-0 mt-1 object-contain"
                    />
                    <div>
                      <h3 className="font-y2k text-xl font-bold text-gray-900 mb-2">Horaires</h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        Tous les mercredis<br />
                        19h30 à 22h30
                      </p>
                    </div>
                  </div>

                  {/* Contact Block - Static */}
                  <div className="flex items-start gap-4">
                    <img 
                      src="https://horizons-cdn.hostinger.com/257434f3-17c6-40ce-944a-86edaf1db41d/223b805be7eff56ee42170f85d377d57.png" 
                      alt="Icône contact" 
                      className="w-10 h-10 flex-shrink-0 mt-1 object-contain"
                    />
                    <div className="flex-grow">
                      <h3 className="font-y2k text-xl font-bold text-gray-900 mb-2">Nous contacter</h3>
                      <div className="text-gray-700 text-lg space-y-2">
                        <p>
                          <a href="mailto:contact@9continent.fr" className="hover:text-[#7A4B9E] transition-colors duration-200 break-all">
                            contact@9continent.fr
                          </a>
                        </p>
                        <p>
                          <a href="tel:+33771177599" className="hover:text-[#7A4B9E] transition-colors duration-200">
                            07 71 17 75 99
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Right Column - Map (approx 65%) */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="lg:col-span-8 w-full"
                >
                  <div className="relative w-full aspect-square">
                    {/* Google Maps embed */}
                    <iframe
                      title="Carte — Collectif Plaisance, 4 allée de l'Ille, 44700 Orvault"
                      src="https://www.google.com/maps?q=Collectif%20Plaisance%2C%204%20all%C3%A9e%20de%20l%27Ille%2C%2044700%20Orvault&z=16&output=embed"
                      className="absolute inset-0 w-full h-full border-0 rounded-xl shadow-lg"
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                    />

                    {/*
                      Overlay décoratif fourni : l'image est agrandie et décalée pour que
                      le cadre violet coïncide exactement avec les bords de la carte
                      (le cadre occupe 85 % de l'image source). Le sticker déborde
                      volontairement, comme un autocollant posé sur la carte.
                    */}
                    <img
                      src="/Carte_plaisance.png"
                      alt=""
                      aria-hidden="true"
                      className="absolute pointer-events-none z-10 select-none max-w-none"
                      style={{
                        width: '117.65%',
                        height: '117.71%',
                        left: '-9.51%',
                        top: '-6.67%'
                      }}
                    />
                  </div>
                </motion.div>

              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default PracticalInfoPage;
